
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Player, parsePlayerStats, parsePlayerSkillLevels } from "@/types/player";
import { Calendar, Hash, ExternalLink, Video as VideoIcon, Youtube } from "lucide-react";
import { Video } from "@/types/video";

interface PlayerCardProps {
  player: Player;
  canEdit: boolean;
}

export const PlayerCard = ({ player, canEdit }: PlayerCardProps) => {
  const skillLevels = parsePlayerSkillLevels(player.skill_levels);
  const overallSkill = Math.round(
    Object.values(skillLevels).reduce((sum, level) => sum + level, 0) / 5
  );

  const playerName = `${player.profile?.first_name || ''} ${player.profile?.last_name || ''}`;

  const getVideoIcon = (source: string) => {
    switch (source) {
      case 'YouTube':
        return <Youtube className="w-3 h-3 text-red-600" />;
      case 'Hudl':
        return <VideoIcon className="w-3 h-3 text-orange-600" />;
      case 'BallerTV':
        return <VideoIcon className="w-3 h-3 text-blue-600" />;
      default:
        return <VideoIcon className="w-3 h-3 text-gray-600" />;
    }
  };

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case 'YouTube':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Hudl':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'BallerTV':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Group videos by source for better organization
  const videoUrls = Array.isArray(player.video_urls) ? player.video_urls : [];
  const videosBySource = videoUrls.reduce((acc, video) => {
    // Handle both Video objects and string URLs
    if (typeof video === 'string') {
      // Legacy string format - convert to Video object
      const source = video.includes('youtube.com') || video.includes('youtu.be') ? 'YouTube' :
                    video.includes('hudl.com') ? 'Hudl' :
                    video.includes('ballertv.com') ? 'BallerTV' : 'Other';
      const videoObj: Video = {
        id: `video-${Math.random().toString(36).substr(2, 9)}`,
        url: video,
        source,
        title: '',
        description: '',
        created_at: new Date().toISOString()
      };
      if (!acc[source]) {
        acc[source] = [];
      }
      acc[source].push(videoObj);
    } else if (video && typeof video === 'object' && 'source' in video) {
      // New Video object format
      if (!acc[video.source]) {
        acc[video.source] = [];
      }
      acc[video.source].push(video as Video);
    }
    return acc;
  }, {} as Record<string, Video[]>) || {};

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-orange-400">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-gradient-to-r from-orange-400 to-blue-400 text-white font-semibold">
                {(player.profile?.first_name?.[0] || '?')}{(player.profile?.last_name?.[0] || '?')}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">{playerName}</CardTitle>
              <Badge variant="secondary" className="mt-1">
                {player.position || 'No Position'}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            {player.jersey_number && (
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Hash className="w-4 h-4 mr-1" />
                {player.jersey_number}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Overall Skill</span>
            <span className="text-sm text-gray-600">{overallSkill}%</span>
          </div>
          <Progress value={overallSkill} className="h-2" />
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Spikes</span>
              <span className="font-medium">{parsePlayerStats(player.stats).spikes}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Serves</span>
              <span className="font-medium">{parsePlayerStats(player.stats).serves}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Blocks</span>
              <span className="font-medium">{parsePlayerStats(player.stats).blocks}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Digs</span>
              <span className="font-medium">{parsePlayerStats(player.stats).digs}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Assists</span>
              <span className="font-medium">{parsePlayerStats(player.stats).assists}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Games</span>
              <span className="font-medium">{player.games_played || 0}</span>
            </div>
          </div>
        </div>

        {/* Enhanced Video Display */}
        {videoUrls && videoUrls.length > 0 && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 mb-3">
              <VideoIcon className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium">Video Highlights ({videoUrls.length})</span>
            </div>
            
            <div className="space-y-3">
              {Object.entries(videosBySource).map(([source, videos]) => (
                <div key={source} className="space-y-2">
                  <div className="flex items-center gap-2">
                    {getVideoIcon(source)}
                    <Badge 
                      variant="outline" 
                      className={`${getSourceBadgeColor(source)} text-xs`}
                    >
                      {source} ({videos.length})
                    </Badge>
                  </div>
                  
                  <div className="space-y-1 ml-5">
                    {videos.map((video, index) => (
                      <Button
                        key={video.id || index}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left h-auto py-2"
                        onClick={() => window.open(video.url, '_blank')}
                      >
                        <div className="flex items-start gap-2 flex-1">
                          <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">
                              {video.title || `${source} Video`}
                            </div>
                            {video.description && (
                              <div className="text-xs text-gray-500 truncate mt-0.5">
                                {video.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Member Since */}
        <div className="flex items-center text-xs text-gray-500 pt-2 border-t">
          <Calendar className="w-3 h-3 mr-1" />
          Member since {player.join_date ? new Date(player.join_date).toLocaleDateString() : 'Unknown'}
        </div>
      </CardContent>
    </Card>
  );
};
